import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import panelApi from "@/lib/panelApi";
import { apiError } from "@/lib/api";
import { useBase } from "@/context/BaseContext";
import { useAutoExpenses } from "@/lib/useAutoExpenses";
import { ExpenseDialog } from "@/components/ExpenseDialog";
import { BaseComparison } from "@/components/BaseComparison";
import { Loading, Empty } from "@/components/DataState";
import { brl, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { buildBalancetePdf } from "@/lib/balancetePdf";
import { useReportLogo } from "@/lib/useLogo";
import { Plus, Pencil, Trash2, Repeat, Cpu, StopCircle, FileText, TrendingUp, Columns3, CalendarClock } from "lucide-react";

const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const TYPE_LABEL = { fixa: "Fixa", variavel: "Variável" };

function useMonthOptions() {
  return useMemo(() => {
    const now = new Date();
    return Array.from({ length: 15 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() + 2 - i, 1);
      return { y: d.getFullYear(), m: d.getMonth() + 1, label: `${MONTHS[d.getMonth()]} de ${d.getFullYear()}` };
    });
  }, []);
}

function Balancete({ list, receitas }) {
  const groups = ["fixa", "variavel"].map((t) => {
    const rows = list.filter((e) => e.type === t);
    const cats = {};
    rows.forEach((e) => { cats[e.category] = (cats[e.category] || 0) + e.value; });
    return { t, total: rows.reduce((a, e) => a + e.value, 0), cats: Object.entries(cats).sort((a, b) => b[1] - a[1]) };
  });
  const total = groups[0].total + groups[1].total;
  const fat = receitas.reduce((a, e) => a + e.value, 0);
  const res = fat - total;
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="expense-balancete">
      {groups.map((g) => (
        <div key={g.t} className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid={`balancete-${g.t}`}>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Despesas {g.t === "fixa" ? "fixas" : "variáveis"}</p>
          <p className={`mt-1 font-mono text-2xl font-bold ${g.t === "fixa" ? "text-sky-300" : "text-amber-300"}`} data-testid={`balancete-${g.t}-total`}>{brl(g.total)}</p>
          <div className="mt-3 space-y-1">
            {g.cats.length === 0 && <p className="text-xs text-slate-600">Nenhuma despesa</p>}
            {g.cats.map(([c, v]) => (
              <div key={c} className="flex justify-between text-sm" data-testid={`balancete-${g.t}-cat-${c}`}>
                <span className="truncate text-slate-400">{c}</span><span className="font-mono text-slate-200">{brl(v)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex flex-col justify-between rounded-xl border border-orange-500/30 bg-orange-500/5 p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-300">Total geral do período</p>
        <p className="font-mono text-4xl font-extrabold text-white" data-testid="balancete-total">{brl(total)}</p>
        <p className="text-xs text-slate-500">{list.filter((e) => e.auto).length} automática(s) · {list.filter((e) => !e.auto).length} lançamento(s) manual(is)</p>
      </div>
      <div className={`flex flex-col justify-between gap-2 rounded-xl border p-5 ${res >= 0 ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"}`} data-testid="balancete-resultado">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-300">Receita x Despesa</p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Faturamento</span><span className="font-mono text-emerald-300" data-testid="balancete-faturamento">{brl(fat)}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Despesas</span><span className="font-mono text-rose-300">- {brl(total)}</span></div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Resultado líquido</p>
          <p className={`font-mono text-3xl font-extrabold ${res >= 0 ? "text-emerald-300" : "text-rose-300"}`} data-testid="balancete-resultado-valor">{brl(res)}</p>
          {fat > 0 && <p className="text-xs text-slate-500">Margem: {Math.round((res / fat) * 1000) / 10}%</p>}
        </div>
      </div>
    </div>
  );
}

export default function Despesas() {
  const months = useMonthOptions();
  const [mIdx, setMIdx] = useState(2);
  const [period, setPeriod] = useState("mensal");
  const [editing, setEditing] = useState(null);
  const [compare, setCompare] = useState(false);
  const { y, m } = months[mIdx];
  const mStr = `${y}-${String(m).padStart(2, "0")}`;
  const { bases, selectedBaseId } = useBase();
  const logo = useReportLogo();
  const qc = useQueryClient();
  const auto = useAutoExpenses(y, m, period);
  const manual = useQuery({
    queryKey: ["panel-expenses", mStr],
    queryFn: async () => (await panelApi.get("/panel/expenses", { params: { month: mStr } })).data?.expenses || [],
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["panel-expenses"] });

  const allManual = (manual.data || []).filter((e) => {
    const day = Number(e.date.slice(8, 10));
    const inQ = period === "mensal" || (period === 1 ? day <= 15 : day >= 16);
    return inQ && (!selectedBaseId || !e.base_id || e.base_id === selectedBaseId);
  });
  const manualList = allManual.filter((e) => e.kind !== "receita");
  const receitas = allManual.filter((e) => e.kind === "receita");
  const list = [...auto.items, ...manualList];
  const categories = [...new Set([...(manual.data || []).filter((e) => e.kind !== "receita").map((e) => e.category), "Aluguel", "Manutenção", "Combustível", "Impostos", "Marketing"])];
  const revCategories = [...new Set([...(manual.data || []).filter((e) => e.kind === "receita").map((e) => e.category), "Faturamento de entregas", "Repasse de plataforma", "Outras receitas"])];
  const baseName = (id) => (id ? bases.find((b) => b.id === id)?.name || "Base removida" : "Todas as bases");

  const save = async (body) => {
    try {
      if (body.id) await panelApi.put(`/panel/expenses/${body.id}`, body);
      else await panelApi.post("/panel/expenses", body);
      toast.success(body.kind === "receita" ? "Faturamento salvo" : "Despesa salva");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (e) => {
    if (!window.confirm(e.recurring ? `Excluir "${e.description}" de TODOS os meses?` : `Excluir "${e.description}"?`)) return;
    try { await panelApi.delete(`/panel/expenses/${e.id}`); toast.success("Despesa excluída"); refresh(); } catch (err) { toast.error(apiError(err)); }
  };
  const stop = async (e) => {
    if (!window.confirm(`Parar de repetir "${e.description}" a partir de ${MONTHS[m - 1]}? Os meses anteriores continuam.`)) return;
    try { await panelApi.post(`/panel/expenses/${e.id}/stop`, { month: mStr }); toast.success("Repetição encerrada"); refresh(); } catch (err) { toast.error(apiError(err)); }
  };
  const btn = (a) => `px-3 py-1.5 text-sm font-medium transition-colors ${a ? "bg-orange-500 text-white" : "bg-slate-900 text-slate-300 hover:bg-slate-800"}`;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const defaultDate = today.startsWith(mStr) ? today : `${mStr}-${period === 2 ? "16" : "01"}`;

  return (
    <div className="space-y-6" data-testid="despesas-page">
      <div className="flex flex-wrap items-center gap-3">
        <select value={mIdx} onChange={(e) => setMIdx(Number(e.target.value))} data-testid="despesas-month" className="h-9 rounded-md border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100">
          {months.map((o, i) => <option key={o.label} value={i}>{o.label}</option>)}
        </select>
        <div className="flex overflow-hidden rounded-lg border border-slate-700">
          {[{ v: "mensal", l: "Mensal" }, { v: 1, l: "1ª Quinzena" }, { v: 2, l: "2ª Quinzena" }].map((p) => (
            <button key={p.v} onClick={() => setPeriod(p.v)} className={btn(period === p.v)} data-testid={`despesas-period-${p.v}`}>{p.l}</button>
          ))}
        </div>
        <span className="text-xs text-slate-500">Base: {baseName(selectedBaseId)}</span>
        <Button variant="outline" className="ml-auto border-slate-700 text-slate-300" data-testid="despesas-pdf" disabled={auto.loading || manual.isLoading}
          onClick={() => buildBalancetePdf({ periodLabel: `${months[mIdx].label} · ${period === "mensal" ? "Mensal" : `${period}ª Quinzena`}`, baseLabel: baseName(selectedBaseId), despesas: list, receitas, logo })
            .save(`balancete-${mStr}-${period === "mensal" ? "mensal" : `q${period}`}.pdf`)}>
          <FileText className="h-4 w-4" /> Balancete em PDF
        </Button>
        <Button variant="outline" className="border-emerald-600/50 text-emerald-300 hover:bg-emerald-500/10" data-testid="receita-add"
          onClick={() => setEditing({ kind: "receita", description: "", category: "", type: "variavel", value: "", date: defaultDate, base_id: selectedBaseId || "", recurring: false })}>
          <TrendingUp className="h-4 w-4" /> Lançar faturamento
        </Button>
        <Button className="bg-orange-500 text-white hover:bg-orange-600" data-testid="despesas-add"
          onClick={() => setEditing({ kind: "despesa", description: "", category: "", type: "fixa", value: "", date: defaultDate, base_id: selectedBaseId || "", recurring: false })}>
          <Plus className="h-4 w-4" /> Nova despesa
        </Button>
      </div>

      {auto.loading || manual.isLoading ? <Loading /> : <Balancete list={list} receitas={receitas} />}

      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-300">Resultado por base</p>
          <Button size="sm" variant="outline" className="h-8 border-slate-700 text-slate-300" onClick={() => setCompare((v) => !v)} data-testid="despesas-compare-toggle">
            <Columns3 className="h-4 w-4" /> {compare ? "Ocultar comparativo" : "Comparar bases"}
          </Button>
        </div>
        {compare && (
          <div className="mt-4">
            <BaseComparison bases={bases} y={y} m={m} period={period} manual={(manual.data || []).filter((e) => {
              const day = Number(e.date.slice(8, 10));
              return period === "mensal" || (period === 1 ? day <= 15 : day >= 16);
            })} />
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900" data-testid="receitas-section">
        <p className="border-b border-slate-800 px-4 py-3 text-xs font-bold uppercase tracking-wider text-emerald-300">Faturamento do período</p>
        {!receitas.length ? <p className="px-4 py-4 text-sm text-slate-500" data-testid="receitas-empty">Nenhum faturamento lançado. Use “Lançar faturamento” para ver o resultado líquido.</p> : (
          <div className="divide-y divide-slate-800">
            {receitas.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm" data-testid={`receita-row-${e.id}`}>
                <span className="w-24 font-mono text-slate-400">{fmtDate(e.date)}</span>
                <span className="flex-1 text-slate-100">{e.description}{e.recurring && <span className="ml-2 inline-flex items-center gap-1 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] text-violet-300"><Repeat className="h-3 w-3" />Mensal</span>}</span>
                <span className="text-slate-400">{e.category}</span>
                <span className="text-slate-500">{baseName(e.base_id)}</span>
                <span className="w-32 text-right font-mono text-emerald-300">{brl(e.value)}</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" onClick={() => setEditing({ ...e, date: e.recurring ? e.start_date : e.date, base_id: e.base_id || "" })} data-testid={`receita-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                  {e.recurring && <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" onClick={() => stop(e)} data-testid={`receita-stop-${e.id}`}><StopCircle className="h-4 w-4" /></Button>}
                  <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300 hover:text-rose-300" onClick={() => remove(e)} data-testid={`receita-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!auto.loading && auto.elsewhere.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-3 text-xs text-slate-400" data-testid="despesas-elsewhere">
          <CalendarClock className="h-4 w-4 text-sky-300" />
          <span>Benefícios sem valor nesta quinzena:</span>
          {auto.elsewhere.map((s) => (
            <span key={s.label} className="rounded bg-slate-800 px-2 py-0.5 text-slate-300" data-testid={`despesas-elsewhere-${s.label}`}>{s.label} · {s.payQ}</span>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
        <table className="w-full min-w-[860px] text-sm" data-testid="despesas-table">
          <thead>
            <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-semibold">Data</th>
              <th className="px-4 py-3 font-semibold">Descrição</th>
              <th className="px-4 py-3 font-semibold">Categoria</th>
              <th className="px-4 py-3 font-semibold">Tipo</th>
              <th className="px-4 py-3 font-semibold">Base</th>
              <th className="px-4 py-3 text-right font-semibold">Valor</th>
              <th className="px-4 py-3 text-right font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {!list.length && <tr><td colSpan={7}><Empty label="Nenhuma despesa no período" /></td></tr>}
            {list.map((e) => (
              <tr key={e.id} className="hover:bg-slate-800/40" data-testid={`despesa-row-${e.id}`}>
                <td className="px-4 py-3 font-mono text-slate-400">{e.auto ? "Período" : fmtDate(e.date)}</td>
                <td className="px-4 py-3 text-slate-100">
                  {e.description}
                  {e.auto && <span className="ml-2 inline-flex items-center gap-1 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-400"><Cpu className="h-3 w-3" />Automática</span>}
                  {e.payQ && <span className="ml-2 inline-flex items-center gap-1 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] text-sky-300" data-testid={`despesa-payq-${e.id}`}><CalendarClock className="h-3 w-3" />{e.payQ}</span>}
                  {e.recurring && <span className="ml-2 inline-flex items-center gap-1 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] text-violet-300"><Repeat className="h-3 w-3" />Mensal</span>}
                </td>
                <td className="px-4 py-3 text-slate-300">{e.category}</td>
                <td className="px-4 py-3"><span className={`rounded px-2 py-0.5 text-xs font-semibold ${e.type === "fixa" ? "bg-sky-500/15 text-sky-300" : "bg-amber-500/15 text-amber-300"}`}>{TYPE_LABEL[e.type]}</span></td>
                <td className="px-4 py-3 text-slate-400">{e.auto ? baseName(selectedBaseId) : baseName(e.base_id)}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-100" data-testid={`despesa-value-${e.id}`}>{brl(e.value)}</td>
                <td className="px-4 py-3">
                  {!e.auto && (
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" onClick={() => setEditing({ ...e, date: e.recurring ? e.start_date : e.date, base_id: e.base_id || "" })} data-testid={`despesa-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                      {e.recurring && <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" title="Parar de repetir a partir deste mês" onClick={() => stop(e)} data-testid={`despesa-stop-${e.id}`}><StopCircle className="h-4 w-4" /></Button>}
                      <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300 hover:text-rose-300" onClick={() => remove(e)} data-testid={`despesa-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ExpenseDialog initial={editing} categories={editing.kind === "receita" ? revCategories : categories} bases={bases} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}
